import { Col, Container, Form, Row } from "react-bootstrap";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import * as axiosInstance from "../../services/axiosInstance";
import Helper from "../../utils/Helper";
import _ from "lodash";
import { useDispatch, useSelector } from "react-redux";
import * as StorageManager from "../../utils/StorageManager";
import constants from "../../constants";
import React, { useEffect, useState } from "react";
import * as authSelectors from "../../selectors/auth";
import clsx from "clsx";
import ReportItemTable from "./components/ReportItemTable";
import ReportSummaryTable from "./components/ReportSummaryTable";
import * as generalSelectors from "../../selectors/general";
import * as userSelectors from "../../selectors/user";
import Image from "react-bootstrap/Image";
import { apiInstance } from "../../services/httpService";
import LoadingOverlay from "../../components/LoadingOverlay";
import {Tooltip} from "react-tooltip";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faCircleInfo} from "@fortawesome/free-solid-svg-icons";

function DailyBusinessReport({ generalData }) {
  const dispatch = useDispatch();
  const [token, setToken] = useState(null);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const isLogout = useSelector((state) => authSelectors.getAuthIsLogout(state));
  const date = new Date();
  const dateNow = date.toISOString().split("T")[0];
  const minDate = new Date();
  minDate.setDate(date.getDate() - 31);
  const minDateFromNow = minDate.toISOString().split("T")[0];
  const [currentDate, setCurrentDate] = useState(dateNow);
  const [calMaxDate, setCalMaxDate] = useState(dateNow);
  const [calMinDate, setCalMinDate] = useState(minDateFromNow);
  const userState = useSelector((state) => state.user);
  const accountSelections = userSelectors.getUserAccountSelections(userState);
  const clientName = userSelectors.getUserName(userState);
  const selectedAccount = _.find(accountSelections, "is_selected");
  const userText =
    userSelectors.getUserAccountSelectionsUserText(selectedAccount);

  useEffect(() => {
    const dailyDate = generalSelectors.getGeneralDateDailyBusinessValue(generalData);
    if (!_.isEmpty(dailyDate)) {
      setCurrentDate(dailyDate);

      const reportDate = new Date(dailyDate);
      reportDate.setDate(reportDate.getDate() - 31);
      setCalMinDate(reportDate.toISOString().split("T")[0])
      setCalMaxDate(dailyDate)
    }
  }, [generalData]);

  useEffect(() => {
    setTokenFunction();
  }, [isLogout]);

  async function setTokenFunction() {
    setToken(await StorageManager.retrieveToken(constants.AUTH_TOKEN));
  }

  const { isFetching, data } = useQuery({
    queryKey: ["daily-business-report", { currentDate }],
    queryFn: async () => {
      try {
        return await axiosInstance.get(
          `clientarea/statement/daily-business?data_date=${currentDate}`,
        );
      } catch (e) {
        Helper.handleHttpError(e, dispatch);
      }
    },
    retryDelay: 15000,
    placeholderData: keepPreviousData,
    enabled: !_.isEmpty(token),
  });

  async function onClickDownloadPDF() {
    if (downloadLoading) {
      return;
    }
    const axios = apiInstance({
      responseType: "blob",
      'contentType': 'application/pdf',
    },{      responseType: "blob",
    });
    try {
      setDownloadLoading(true);
      const response = await axios.get(
        `clientarea/statement/daily-business?data_date=${currentDate}&pdf=1`,
      );
      const data = new Blob([response.data], { type: "application/pdf" });
      let downloadUrl = window.URL.createObjectURL(data);
      let downloadLink = document.createElement("a");
      downloadLink.href = downloadUrl;
      downloadLink.setAttribute("download", `daily-business-done-${currentDate}.pdf`);
      downloadLink.click();
    } catch (error) {
      axiosInstance.httpErrorHelpers(error);
    } finally {
      setDownloadLoading(false);
    }
  }

  const reportItems = _.get(data, ["items"], []);
  const summaryItems = _.get(data, ["summary"], []);

  const onChangeDate = (e) => {
    if (_.isEmpty(e.target.value)) {
      setCurrentDate(calMaxDate);
      return;
    }
    setCurrentDate(e.target.value);
  };

  return (
      <Container fluid>
        <div className="mb-3">
          <Row>
            <Col md={12} lg={7}>
              <div
                  className={clsx(
                      "custom-table-title-container justify-content-lg-start justify-content-center",
                  )}
              >
                <p> {clientName} </p>
                <p> ( {userText} ) </p>
              </div>
            </Col>
            <Col md={12} lg={5}>
              <div className="d-flex align-items-center justify-content-lg-end  justify-content-between mt-md-0 mt-2">
                <Tooltip id="datepicker-tooltip" />
                <FontAwesomeIcon
                    data-tooltip-id="datepicker-tooltip" data-tooltip-content="Historical data only available for 30 days."
                    className="me-2"
                    icon={faCircleInfo}
                    style={{marginLeft: "24px", color: "#b99559", cursor: "pointer"}}
                />
                <Form.Control
                    type="date"
                    min={calMinDate}
                    max={calMaxDate}
                    className="custom-settlement-datepicker-white"
                    value={currentDate}
                    onChange={onChangeDate}
                />
                <a
                    className="download-btn ms-2"
                    onClick={() => onClickDownloadPDF()}
                >
                  <Image
                      src="/img/icon/download.png"
                      style={{width: "18px", objectFit: "contain"}}
                  />
                  <LoadingOverlay isSemiLoading={true} loading={downloadLoading}></LoadingOverlay>

                </a>
              </div>
            </Col>
          </Row>
        </div>
        <div
            className={"primary-border-table-border"}
            style={{backgroundColor: "white"}}
        >
          <div
              className={clsx(
                  "d-flex custom-card-label-container align-items-center justify-content-center",
              )}
          >
            <h2>Daily Business Done</h2>
          </div>
          <div className={clsx("custom-table-info-container padding-12px")}>
            <div className={"responsive-table-border table-w-1400 mt-3"}>
              <ReportItemTable
                  loading={isFetching}
                  data={reportItems}
              ></ReportItemTable>
            </div>
          </div>

          <div
              className={clsx(
                  "d-flex custom-card-label-container align-items-center justify-content-center",
              )}
          >
            <h2>Summary</h2>
          </div>
          <div className={clsx("custom-table-info-container padding-12px")}>
            <div className={"responsive-table-border table-w-1400 mt-3"}>
              <ReportSummaryTable
                  loading={isFetching}
                  data={summaryItems}
              ></ReportSummaryTable>
            </div>
          </div>
        </div>
      </Container>
  );
}

export default DailyBusinessReport;
